import React, { FC, useEffect, useState } from 'react';
import { useUser } from 'store/User/Context';
import styled from 'styled-components';
import { useWindowWidth } from 'utils/useWindowWidth';

import Icon, { IconEnum } from 'components/common/Icon';
import AutocompleteInput from 'components/common/Inputs/AutocompleteInput/AutocompleteInput';

const Wrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-direction: column;
    background-color: var(--color-white);
    border-radius: var(--box-border-radius);

    color: ${({ theme }) => theme.text};
    margin: 15px auto;

    @media (min-width: 991px) {
        flex-direction: row;
    }
`;

const List = styled.ul`
    list-style: none;
    display: flex;
    padding: 0;
`;

const ListElement = styled.li`
    font-size: 14px;
    margin: 0 3px;
`;

const Button = styled.button<ButtonTypes>`
    color: var(--color-primary);
    font-weight: normal;
    background: var(--color-alt-second);
    border-radius: 50%;
    min-width: 40px;
    height: 40px;
    border: none;
    outline: none;
    cursor: pointer;
    font-size: 14px;
    margin: 0 2px;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        background-color: ${(props) => props.darkMode ? 'var(--color-primary)' : 'var(--color-alt)'};
        color: var(--color-white);

        span {
            filter: ${(props) => props.darkMode ? 'unset !important' : 'invert(100%) sepia(100%) saturate(0%) hue-rotate(79deg) brightness(105%) contrast(102%)'};
        }
    }

    &.active {
        background: ${(props) => props.darkMode ? 'var(--color-primary)' : 'var(--color-alt)'};
        font-weight: 800;
        color: var(--color-white);
    }
`;

const AutoCompleteWrapper = styled.div`
    position: relative;
    width: 235px;

    & > div {
        background: transparent;
        gap: 0;
        margin-left: 15px;

        p {
            margin-right: 3px;
        }

        input:hover,
        &:hover {
            cursor: pointer;
            caret-color: transparent;
        }
    }

    .focus {
        border: none;
    }

    & > div > span {
        background: transparent;
    }

    .action {
        right: 33px;
        top: 10px;
    }

    span {
        padding: 0;
        cursor: pointer;
        caret-color: transparent;
        input {
            font-weight: 600;
        }

        &:hover {
            cursor: pointer;
            caret-color: transparent;
        }
    }
`;

const ListContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 10px 20px;

    @media (min-width: 991px) {
        margin-top: 0;
    }
`;

const NoPaginationBottomSpace = styled.div`
  height: 40px;
`;

interface ButtonTypes {
    darkMode?: boolean;
}

interface PaginationProperties {
    onChangePage: (currentPage: number, rows?: number) => void;
    amountSites: number;
    pageNumber?: number;
}

const Pagination: FC<PaginationProperties> = ({ onChangePage, amountSites, pageNumber = 1 }) => {
    const { user } = useUser();
    const initialRows = 25;
    const [currentPage, setCurrentPage] = useState(pageNumber);
    const [amountRows, setAmountRows] = useState(initialRows);
    const width = useWindowWidth();
    const visiblePagesDesktop = width >= 991 ? 7 : 3;

    const changeCurrentPage = (page: number, rows?: number, avoidScrollToTop = false) => {
        setCurrentPage(page);
        onChangePage(page, rows || amountRows);

        if (!avoidScrollToTop) {
            setTimeout(() => {
                document.body.scrollTo({ top: 0 });
            }, 300);
        }
    };

    const handleChangeRows = (rows?: string | number) => {
        const newAmountRows = Number(rows);
        setAmountRows(newAmountRows);

        changeCurrentPage(1, newAmountRows, currentPage === 1 && newAmountRows > amountRows);
    };

    useEffect(() => {
        setCurrentPage(pageNumber);
    }, [pageNumber]);

    useEffect(() => {
        if (amountRows !== initialRows || currentPage !== pageNumber) {
            onChangePage(currentPage, amountRows);
        }
    }, [amountRows]);

    const getVisiblePages = () => {
        const halfRange = Math.floor(visiblePagesDesktop / 2);
        const totalVisiblePages = Math.min(visiblePagesDesktop, amountSites);

        if (currentPage <= halfRange) {
            return Array.from({ length: totalVisiblePages }, (_, i) => i + 1);
        }

        if (currentPage >= amountSites - halfRange) {
            return Array.from(
                { length: totalVisiblePages },
                (_, i) => amountSites - totalVisiblePages + i + 1
            );
        }

        return Array.from(
            { length: totalVisiblePages },
            (_, i) => currentPage - halfRange + i
        );
    };

    const visiblePages = getVisiblePages();

    return visiblePages.length > 1
        ? <Wrapper>
            {window.innerWidth >= 991
                ? <AutoCompleteWrapper>
                    <AutocompleteInput
                        oneLine
                        goTop
                        value={amountRows}
                        onChange={handleChangeRows}
                        prefix="Wyświetlaj"
                        options={[
                            { value: 25, label: '25 pozycji' },
                            { value: 50, label: '50 pozycji' },
                            { value: 100, label: '100 pozycji' },
                            { value: 200, label: '200 pozycji' },
                            { value: 500, label: '500 pozycji' }
                        ]}
                    />
                </AutoCompleteWrapper>
                : null}
            <ListContainer>
                {currentPage !== 1 &&
                    <>
                        <Button className={'pagination-btn'} darkMode={user.isDarkMode}
                            onClick={() => changeCurrentPage(1)}>
                            <Icon icon={IconEnum.ANGLE_LEFT_TO_LINE}/>
                        </Button>
                        <Button className={'pagination-btn'} darkMode={user.isDarkMode}
                            onClick={() => changeCurrentPage(currentPage - 1)}>
                            <Icon icon={IconEnum.ANGLE_LEFT}/>
                        </Button>
                    </>
                }
                {visiblePages.map((page) => <List key={page}>
                    <ListElement key={page}>
                        <Button
                            darkMode={user.isDarkMode}
                            key={page}
                            className={page === currentPage ? 'active pagination-btn' : 'pagination-btn'}
                            onClick={() => changeCurrentPage(page)}
                        >
                            {page}
                        </Button>
                    </ListElement>
                </List>)}
                {currentPage !== amountSites
                    ? <>
                        <Button className={'pagination-btn'} darkMode={user.isDarkMode}
                            onClick={() => changeCurrentPage(currentPage + 1)}>
                            <Icon icon={IconEnum.ANGLE_RIGHT}/>
                        </Button>
                        <Button className={'pagination-btn'} darkMode={user.isDarkMode}
                            onClick={() => changeCurrentPage(amountSites)}>
                            <Icon icon={IconEnum.ANGLE_RIGHT_TO_LINE}/>
                        </Button>
                    </>
                    : null
                }
            </ListContainer>
        </Wrapper>
        : <NoPaginationBottomSpace/>;
};

export default Pagination;
